/**
 * Создание задачи на новом бэке (`POST /v2/tasks`).
 *
 * Обвязка повторяет `TaskCreateModal`: портал в body, одна общая `<form>`,
 * затемнение; закрыть можно только крестиком или Esc — через подтверждение.
 * Раскладка одна и адаптивная:
 * порог 620px описан в прототипе и живёт внутри самой формы, отдельного
 * мобильного экрана нет. Отличается только модель — `useTaskV2CreateForm` вместо
 * `useTaskCreateForm`, потому что у v2 UUID-и вместо числовых id Bitrix и
 * другой набор полей в контракте создания.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { ConfirmCloseModal } from './ConfirmCloseModal';
import {
  useTaskV2CreateForm,
  type TaskV2CreateFormInitialData,
} from './v2/useTaskV2CreateForm';
import { TaskV2CreateModalDesktop } from './v2/TaskV2CreateModalDesktop';

interface TaskV2CreateModalProps {
  open: boolean;
  onClose: () => void;
  /** Предзаполнение из клика по слоту календаря: дедлайн и часы плана. */
  initialData?: TaskV2CreateFormInitialData;
}

export function TaskV2CreateModal({
  open,
  onClose,
  initialData,
}: TaskV2CreateModalProps) {
  const form = useTaskV2CreateForm({ open, onClose, initialData });
  const isSubmitting = form.isSubmitting;
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmCloseOpen(false);
    }
  }, [open]);

  // Esc не закрывает форму, а открывает подтверждение; повторный Esc его отменяет.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        setConfirmCloseOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, isSubmitting]);

  if (!open) {
    return null;
  }

  return (
    <>
      {createPortal(
        <form onSubmit={form.submit}>
          <div className="fixed inset-0 z-[9998] flex justify-center overflow-y-auto bg-black/50 px-3 py-4 lg:p-6 scrollbar-primary animate-in fade-in-0 duration-300">
            <TaskV2CreateModalDesktop
              form={form}
              onRequestClose={() => {
                if (!isSubmitting) {
                  setConfirmCloseOpen(true);
                }
              }}
            />
          </div>
        </form>,
        document.body
      )}

      <ConfirmCloseModal
        open={confirmCloseOpen}
        onCancel={() => setConfirmCloseOpen(false)}
        onConfirm={() => {
          setConfirmCloseOpen(false);
          onClose();
        }}
      />
    </>
  );
}
